import { useEffect, useMemo, useState } from 'react';

import {
  faExclamationCircle,
  faLock,
  faLockOpen
} from '@fortawesome/pro-light-svg-icons';
import { useSelector } from 'hooks';
import { isNil } from 'ramda';
import { useTranslation } from 'react-i18next';

import { ErrorDetail, YearClosureProgressData } from '../types';

import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';

import { FontAwesomeSvgIcon } from 'components/shared';
import { Tooltip } from 'components/shared';
import { CustomDataGridAction } from 'components/shared/DataGrid/components/customDataGridAction';

import { YearClosureProgress } from '../components/YearClosureProgress';

interface Params {
  openYear: (id, row) => void;
  closeYear: (id, row) => void;
  onCurrentYearErrorClick: () => void;
  onNextYearErrorClick: () => void;
  currentYearProgress?: YearClosureProgressData | null;
  nextYearProgress?: YearClosureProgressData | null;
  currentYearErrors?: ErrorDetail[];
  nextYearErrors?: ErrorDetail[];
}

const hasFinishedAll = (currentYearProgress, nextYearProgress) => {
  if (!currentYearProgress || !nextYearProgress) {
    return true;
  }
  return !!(currentYearProgress.hasFinished && nextYearProgress.hasFinished);
};

export const useGetColumnDefs = (params: Params): GridColDef[] => {
  const {
    openYear,
    closeYear,
    onCurrentYearErrorClick,
    onNextYearErrorClick,
    currentYearProgress,
    nextYearProgress,
    currentYearErrors,
    nextYearErrors
  } = params;
  const { t } = useTranslation('common');
  const [isActionHidden, setIsActionHidden] = useState(false);
  const { hasAssignOrgGrants } = useSelector(({ grants }) => grants);

  useEffect(() => {
    setIsActionHidden(!hasFinishedAll(currentYearProgress, nextYearProgress));
  }, [currentYearProgress, nextYearProgress]);

  const openHandler = (id, row) => {
    openYear?.(id, row);
  };

  const closeHandler = (id, row) => {
    closeYear?.(id, row);
  };

  const hasGrantsToOpenYear = () => {
    return hasAssignOrgGrants;
  };

  return useMemo(
    () => [
      {
        field: 'year',
        headerName: t('year'),
        type: 'number',
        valueFormatter: (params) => params.value,
        flex: 1,
        minWidth: 80
      },
      {
        field: 'isOpenedStatus',
        headerName: t('status'),
        type: 'boolean',
        filterable: false,
        renderCell: ({ value }) => (
          <>
            {value
              ? t('yearClosure.openedStatus')
              : t('yearClosure.closedStatus')}
          </>
        ),
        flex: 1
      },
      {
        field: 'unrealizedDuties',
        headerName: t('yearClosure.unrealized_duty_count'),
        type: 'number',
        align: 'center',
        headerAlign: 'center',
        valueFormatter: (params) => params.value,
        renderHeader: () => (
          <Box alignItems="center" display="flex">
            <Tooltip
              title={t('yearClosure.unrealized_duty_count_info_icon_text')}
            >
              <FontAwesomeSvgIcon color="warning" icon={faExclamationCircle} />
            </Tooltip>
            <Box ml={1}>{t('yearClosure.unrealized_duty_count')}</Box>
          </Box>
        ),
        flex: 1,
        minWidth: 220
      },
      {
        field: 'customActions',
        headerName: t('actions'),
        renderCell: (params) => (
          <>
            {params.row.isOpenedStatus ? (
              isActionHidden ? null : (
                <CustomDataGridAction
                  action={{
                    icon: faLock,
                    onClick: (id, row) => closeHandler(id, row),
                    tooltipTitle: t('yearClosure.close_year'),
                    disabled: true
                  }}
                  key="openYear"
                  params={params}
                />
              )
            ) : hasGrantsToOpenYear() ? (
              isActionHidden ? null : (
                <CustomDataGridAction
                  action={{
                    icon: faLockOpen,
                    onClick: (id, row) => openHandler(id, row),
                    tooltipTitle: t('yearClosure.open_year'),
                    disabled: isActionHidden
                  }}
                  key="closeYear"
                  params={params}
                />
              )
            ) : null}
            {params.row.isOpenedStatus ? (
              <Box display="flex" flexDirection="column" ml={1}>
                {!isNil(currentYearProgress) || !isNil(nextYearProgress) ? (
                  <YearClosureProgress
                    onErrorClick={onCurrentYearErrorClick}
                    progress={{
                      hasFinished: true,
                      dutyLinesProgress: 100,
                      dutyLinesTotal: 100,
                      errorDetails: [],
                      dutyLinesErrors: 0
                    }}
                    errors={currentYearErrors}
                    title={t('yearClosure.close_current_year')}
                  />
                ) : null}
                {!isNil(currentYearProgress) ? (
                  <YearClosureProgress
                    onErrorClick={onCurrentYearErrorClick}
                    progress={currentYearProgress}
                    errors={currentYearErrors}
                    title={t('yearClosure.calculate_current_year')}
                  />
                ) : null}
                {!isNil(nextYearProgress) ? (
                  <YearClosureProgress
                    onErrorClick={onNextYearErrorClick}
                    progress={nextYearProgress}
                    errors={nextYearErrors}
                    title={t('yearClosure.calculate_next_year')}
                  />
                ) : null}
              </Box>
            ) : null}
          </>
        ),
        flex: 1,
        minWidth: 410
      }
    ],
    [
      t,
      openYear,
      closeYear,
      isActionHidden,
      nextYearProgress,
      currentYearProgress,
      currentYearErrors,
      nextYearErrors
    ]
  );
};
